import {put, call, all} from 'redux-saga/effects';
import RedFlag from "../../entities/redFlag";
import * as Actions from "../actions/redFlagActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type RedFlagAction = {
    redFlagId: string;
    redFlag: RedFlag;
    redFlags: RedFlag[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneRedFlag(action: RedFlagAction) {
	const session = tabSession.captureGeneration();
	try {
		const redFlag: RedFlag = yield call(api.findOne, 'redFlag', action.redFlagId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.REDFLAG_FETCH_ONE_SUCCESS, redFlagId: action.redFlagId, redFlag, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.REDFLAG_FETCH_ONE_FAILURE, redFlagId: action.redFlagId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {RedFlagAction} action
 */
export function* saveRedFlag(action: RedFlagAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ REDFLAG_SAVE saga', action.redFlag?.id);

		if (action.redFlag === undefined || action.redFlag === null) {
			console.error('saveRedFlag received a null');
			throw new Error('saveRedFlag received a null');
		}

		if (!(action.redFlag instanceof RedFlag)) {
			action.redFlag = new RedFlag(action.redFlag);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.redFlag.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.redFlag);
			const addResult = yield call(idb.add, 'redFlag', action.redFlag);
			if (addResult.isErr()) {
				console.error(`⮂ saveRedFlag: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertRedFlagSave(action.redFlag, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.REDFLAG_SAVE_FAILURE, redFlag: action.redFlag, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.REDFLAG_SAVE, redFlag: action.redFlag, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertRedFlagSave(action.redFlag, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.REDFLAG_SAVE_FAILURE, redFlag: action.redFlag, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {RedFlagAction} action
 * @param {RedFlag[]} action.redFlags
 */
export function* saveManyRedFlags(action: RedFlagAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ REDFLAG_SAVE_MANY saga', action.redFlags.map(redFlag => redFlag.id).join(','));

        if (!Array.isArray(action.redFlags)) {
            console.error('saveManyRedFlags expected an array, received', action.redFlags);
            throw new Error('saveManyRedFlags expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveRedFlag), under these queue keys
		const dispatchedAddedOns: number[] = action.redFlags.map(redFlag => redFlag?.addedOn);
		const revertAll = () => action.redFlags.map((redFlag, i) =>
			put(Actions.revertRedFlagSave(redFlag, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: RedFlag[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.redFlags.length; i++) {
				const redFlag = action.redFlags[i];
				redFlag.persisted = false;
				ensureOperationId(redFlag);
				toSave.push(redFlag);
			}

			const addResult = yield call(idb.add, 'redFlag', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyRedFlags: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.REDFLAG_SAVE_MANY_FAILURE, redFlags: action.redFlags, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.REDFLAG_SAVE_MANY, redFlags: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.REDFLAG_SAVE_MANY_FAILURE, redFlags: action.redFlags, status: 500, err});

			console.error(`Error saving ${action.redFlags.length} redFlags to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {RedFlagAction} action
 */
export function* removeRedFlag(action: RedFlagAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.redFlag === undefined || action.redFlag === null || !(action.redFlag instanceof RedFlag)) {
			console.error('removeRedFlag expected type RedFlag, received', action.redFlag);
			reportQueued(false);
			throw new Error('removeRedFlag expected type RedFlag');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.redFlag.status = 'D';
		action.redFlag.addedOn = newHRTimestamp();
		action.redFlag.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.redFlag);
		const addResult = yield call(idb.add, 'redFlag', action.redFlag);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeRedFlag: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.REDFLAG_REMOVE_FAILURE, redFlag: action.redFlag, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.REDFLAG_REMOVE, redFlag: action.redFlag});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.REDFLAG_REMOVE_FAILURE, redFlag: action.redFlag, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this RedFlag.
	*
	* @param {RedFlagAction} action
	*/
export function* restoreRedFlag(action: RedFlagAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreRedFlag expected getCurrentUserId() to return a value');
        return;
    }

	if (action.redFlag === undefined || action.redFlag === null) {
        console.error('restoreRedFlag expected a RedFlag, received null');
        throw new Error('restoreRedFlag expected a RedFlag, received null');
    }
    else if (!(action.redFlag instanceof RedFlag)) action.redFlag = new RedFlag(action.redFlag); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.redFlag.status = 'U';
		action.redFlag.addedOn = newHRTimestamp();
		action.redFlag.addedBy = userId;
		action.redFlag.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.redFlag);
		const addResult = yield call(idb.add, 'redFlag', action.redFlag);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreRedFlag: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.REDFLAG_RESTORE_FAILURE, redFlag: action.redFlag, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.REDFLAG_RESTORE, redFlag: action.redFlag});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.REDFLAG_RESTORE_FAILURE, redFlag: action.redFlag, status: 500, err});

		console.error('Error restoring from database', err);
	}
}